import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import LoginPage from "./core/pages/LoginPage/LoginPage";
import DashboardPage from "./core/pages/DashboardPage/DashboardPage";
import DeviceDetailPage from "./core/pages/CameraDetailPage/DeviceDetailPage";
import Loading from "./core/components/Loading/Loading";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "@coreui/coreui/dist/css/coreui.min.css";
import UserManagementPage from "./core/pages/UserManagementPage/UserManagementPage";
import EmailVerificationPage from "./core/pages/EmailVerificationPage/EmailVerificationPage";
import TruckFleetPage from "./core/pages/TruckFleetPage/TruckFleetPage";
import axios from "axios";
import { useTranslation } from "react-i18next";
import DocumentationPage from "./core/pages/DocumentationPage/DocumentationPage";
import MarkupPage from "./core/pages/MarkupPage/MarkupPage";
import AlertsPage from "./core/pages/AlertsPage/AlertsPage";
import MultiMonitorPage from "./core/pages/MultiMonitorPage/MultiMonitorPage";
import ImuViewerPage from "./core/pages/ImuViewerPage/ImuViewerPage";
import TruckProofsPage from "./core/pages/TruckProofsPage/TruckProofsPage";
import OmnicommMapPage from "./core/pages/OmnicommMapPage/OmnicommMapPage";
import SuspensionGuard from "./core/components/SuspensionGuard/SuspensionGuard";

import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/kk";

const PrivateRoute = ({ children, isAuthenticated }) => {
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const StaffRoute = ({ children, isAuthenticated }) => {
  const isStaff = localStorage.getItem("is_staff") === "true";

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isStaff) return <Navigate to="/dashboard" replace />;

  return children;
};

const SuperuserRoute = ({ children, isAuthenticated }) => {
  const isSuperuser = localStorage.getItem("is_superuser") === "true";

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isSuperuser) return <Navigate to="/dashboard" replace />;

  return children;
};

function App() {
  const { t, i18n } = useTranslation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAppReady, setIsAppReady] = useState(false);

  useEffect(() => {
    const currentLang = i18n.resolvedLanguage || i18n.language;
    if (currentLang && currentLang.startsWith("ru")) {
      dayjs.locale("ru");
    } else if (currentLang && currentLang.startsWith("kk")) {
      dayjs.locale("kk");
    } else {
      dayjs.locale("en");
    }
  }, [i18n.language, i18n.resolvedLanguage]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          const isAuthRequest = originalRequest.url.includes("/api/token/");

          if (!isAuthRequest) {
            originalRequest._retry = true;
            const refreshToken = localStorage.getItem("refreshToken");

            if (refreshToken) {
              try {
                const res = await axios.post("/api/token/refresh/", {
                  refresh: refreshToken,
                });

                if (res.data.access) {
                  localStorage.setItem("token", res.data.access);
                  if (res.data.refresh) {
                    localStorage.setItem("refreshToken", res.data.refresh);
                  }

                  originalRequest.headers["Authorization"] = `Bearer ${res.data.access}`;

                  return axios(originalRequest);
                }
              } catch (refreshError) {
                console.warn("Refresh token expired or invalid");
              }
            }

            if (localStorage.getItem("token")) {
              localStorage.removeItem("token");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("is_staff");
              localStorage.removeItem("is_superuser");
              localStorage.removeItem("can_do_markup");
              localStorage.removeItem("organizationName");
              localStorage.removeItem("organizationId");
              setIsAuthenticated(false);

              toast.warn(
                t("login.session_expired") ||
                "Срок действия сессии истек. Войдите снова."
              );
            }
          }
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [t]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const REFRESH_INTERVAL = 6 * 60 * 60 * 1000;
    const refreshTimer = setInterval(async () => {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        try {
          const res = await axios.post("/api/token/refresh/", {
            refresh: refreshToken,
          });
          if (res.data.access) {
            localStorage.setItem("token", res.data.access);
            if (res.data.refresh) {
              localStorage.setItem("refreshToken", res.data.refresh);
            }
            console.log("Токен успешно продлен в фоновом режиме");
          }
        } catch (error) {
          console.warn("Не удалось фоново обновить токен", error);
        }
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(refreshTimer);
  }, [isAuthenticated]);

  useEffect(() => {
    const initApp = async () => {
      const savedLang = localStorage.getItem("i18nextLng");
      if (!savedLang) {
        try {
          const response = await axios.get("https://ipapi.co/json/", {
            timeout: 2000,
          });
          const country = response.data.country_code;

          let lang = "en";
          if (country === "KZ") {
            lang = "kk";
          } else if (["RU", "BY", "UA", "KG", "UZ"].includes(country)) {
            lang = "ru";
          }
          i18n.changeLanguage(lang);
        } catch {
          console.log("Auto-language detection skipped.");
        }
      }

      const token = localStorage.getItem("token");
      if (token) {
        setIsAuthenticated(true);
      } else {
        try {
          const response = await axios.post("/api/desktop-login/");
          if (response.data.access) {
            localStorage.setItem("token", response.data.access);
            localStorage.setItem("refreshToken", response.data.refresh);
            localStorage.setItem("is_staff", response.data.is_staff);
            localStorage.setItem("can_do_markup", response.data.can_do_markup);
            localStorage.setItem("is_suspended", response.data.organization.is_suspended);
            localStorage.setItem("org_data", JSON.stringify(response.data.organization));
            
            if (response.data.organization) {
              localStorage.setItem(
                "organizationName",
                response.data.organization.name,
              );
              localStorage.setItem(
                "organizationId",
                response.data.organization.id,
              );
            }
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.log("Desktop auto-login skipped.");
        }
      }
      setIsAppReady(true);
    };

    initApp();
  }, [i18n]);

  if (!isAppReady) return <Loading name="Запуск..." />;

  return (
    <>
      <Router>
        <SuspensionGuard isAuthenticated={isAuthenticated}>
          <Routes>
            <Route
              path="/login"
              element={
                isAuthenticated ? (
                  <Navigate to="/dashboard" replace />
                ) : (
                  <LoginPage setIsAuthenticated={setIsAuthenticated} />
                )
              }
            />
            <Route
              path="/verify-email/:uid/:token"
              element={<EmailVerificationPage />}
            />

            <Route
              path="/dashboard"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <DashboardPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/camera/:cameraId"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <DeviceDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/users"
              element={
                <StaffRoute isAuthenticated={isAuthenticated}>
                  <UserManagementPage />
                </StaffRoute>
              }
            />
            <Route
              path="/trucks"
              element={
                <StaffRoute isAuthenticated={isAuthenticated}>
                  <TruckFleetPage />
                </StaffRoute>
              }
            />
            <Route
              path="/documentation"
              element={
                <StaffRoute isAuthenticated={isAuthenticated}>
                  <DocumentationPage />
                </StaffRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <AlertsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/truck-proofs"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <TruckProofsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/markup"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <MarkupPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/multi-monitor"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <MultiMonitorPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/imu-viewer"
              element={
                <SuperuserRoute isAuthenticated={isAuthenticated}>
                  <ImuViewerPage />
                </SuperuserRoute>
              }
            />
            <Route
              path="*"
              element={
                <Navigate
                  to={isAuthenticated ? "/dashboard" : "/login"}
                  replace
                />
              }
            />
            <Route
              path="/omnicomm-map"
              element={
                <PrivateRoute isAuthenticated={isAuthenticated}>
                  <OmnicommMapPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </SuspensionGuard>
      </Router>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
      />
    </>
  );
}

export default App;